import { api } from './client';
import type { ChatSummary, Message, PaginatedResponse } from '@/types/domain';

export async function fetchChats(
  options: { archived?: boolean } = {},
): Promise<{ results: ChatSummary[] }> {
  // F-170 B1.7 — archive tab: `?archived=true` lists archived chats only,
  // `?archived=false` (the default) lists active ones. Backend
  // `POST /chats/<id>/archive/` lives behind B0.13.
  const query: Record<string, string | undefined> = {};
  if (typeof options.archived === 'boolean') {
    query.archived = options.archived ? 'true' : 'false';
  }
  return api.get('/chats/', { query });
}

export async function archiveChat(id: number): Promise<void> {
  await api.post(`/chats/${id}/archive/`);
}

export async function unarchiveChat(id: number): Promise<void> {
  await api.post(`/chats/${id}/unarchive/`);
}

export async function fetchChat(id: number): Promise<ChatSummary> {
  return api.get(`/chats/${id}/`);
}

export async function openChatWithSpecialist(
  specialistSlug: string,
  options: { firstText?: string; anonymous?: boolean } = {},
): Promise<ChatSummary> {
  return api.post('/chats/', {
    body: {
      specialist_slug: specialistSlug,
      text: options.firstText,
      is_anonymous: options.anonymous ?? false,
    },
  });
}

export async function fetchMessages(
  chatId: number,
  cursor?: string | null,
): Promise<PaginatedResponse<Message>> {
  return api.get(`/chats/${chatId}/messages/`, { query: { cursor: cursor ?? undefined } });
}

export interface SendMessageInput {
  kind: 'text' | 'image';
  text?: string;
  image_upload_id?: string;
  client_nonce?: string;
}

export async function sendMessage(chatId: number, input: SendMessageInput): Promise<Message> {
  return api.post(`/chats/${chatId}/messages/`, { body: input });
}

export async function markRead(chatId: number, upToMessageId: number): Promise<void> {
  await api.post(`/chats/${chatId}/read/`, { body: { up_to_message_id: upToMessageId } });
}

export async function revealClient(chatId: number): Promise<void> {
  await api.post(`/chats/${chatId}/reveal/`);
}

export async function uploadImage(file: File): Promise<{ upload_id: string; url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/uploads/image/', { body: fd });
}
